import React from 'react';
import { AlertTriangleIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, CircleAlertIcon, InboxIcon, LockIcon, SearchIcon, TrendingDownIcon, TrendingUpIcon, XIcon } from 'lucide-react';
import { Avatar } from '../Avatar';
import { Button } from '../ui/Button';
import { adminUsers, type AdminUser } from '../../data/admin';
import { people } from '../../data/people';
import { useLocale } from '../../contexts/LocaleContext';

/* ---------------------------------------------------------------- *
 * Layout primitives
 * ---------------------------------------------------------------- */

export function Panel({
  title,
  hint,
  action,
  children,
  className = ''
}: {
  title?: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return <section className={`rounded-card border border-sand-line bg-white/80 ${className}`} data-id="element-270" data-mp="2oscjqg9wwgrr">
      {(title || action) && <header className="flex items-start justify-between gap-3 border-b border-sand-line px-3 py-2" data-id="element-271" data-mp="2supeyar18waa">
          <div className="min-w-0" data-id="element-272" data-mp="1g83q0wx1qfmp">
            {title && <h2 className="text-[14px] font-bold leading-tight text-deep" data-id="element-273" data-mp="1besgup4rhmrm">
                {title}
              </h2>}
            {hint && <p className="mt-[2px] text-[11.5px] leading-snug text-muted" data-id="element-274" data-mp="11hmpdfs8jtqv">{hint}</p>}
          </div>
          {action && <div className="shrink-0" data-id="element-275" data-mp="1z43j6b1rfp7v">{action}</div>}
        </header>}
      <div className="p-3" data-id="element-276" data-mp="1u6biosn1uus2">{children}</div>
    </section>;
}
export function AdminPageTitle({
  title,
  subtitle,
  action
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-277" data-mp="1k8dx6cwkrc35">
      <div className="min-w-0" data-id="element-278" data-mp="drscwzbtw775">
        <h1 className="text-[22px] font-bold leading-tight text-deep" data-id="element-279" data-mp="ds22wmwviecu">{title}</h1>
        {subtitle && <p className="mt-1 max-w-[70ch] text-[12.5px] leading-snug text-muted" data-id="element-280" data-mp="8rntp1jjcyd8">
            {subtitle}
          </p>}
      </div>
      {action && <div className="flex shrink-0 items-center gap-2" data-id="element-281" data-mp="2wwtfrrxlj3vy">{action}</div>}
    </div>;
}
export function AdminTabs({
  tabs,
  active,
  onChange
}: {
  tabs: {
    id: string;
    label: string;
    count?: number;
  }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return <div role="tablist" className="flex flex-wrap items-center gap-1 border-b border-sand-line" data-id="element-282" data-mp="2ry013w6kp6u7">
      {tabs.map(tab => {
      const selected = tab.id === active;
      return <button key={tab.id} type="button" role="tab" aria-selected={selected} onClick={() => onChange(tab.id)} className={`-mb-[1px] flex min-h-[40px] items-center gap-2 border-b-2 px-3 text-[13px] font-medium transition-colors duration-150 ease-brand ${selected ? 'border-gold text-deep' : 'border-transparent text-muted hover:text-deep'}`} data-id="element-283" data-mp="2i26qiiceznf2">
            {tab.label}
            {tab.count !== undefined && <span className={`bee-num rounded-pill px-[7px] py-[1px] text-[10.5px] font-bold ${selected ? 'bg-gold text-deep' : 'bg-sand-deep text-muted'}`} data-id="element-284" data-mp="1bns70dt8ee2q">
                {tab.count}
              </span>}
          </button>;
    })}
    </div>;
}

/* ---------------------------------------------------------------- *
 * Status and identity
 * ---------------------------------------------------------------- */

type Tone = 'neutral' | 'positive' | 'warning' | 'critical' | 'gold';
const toneClass: Record<Tone, string> = {
  neutral: 'border-sand-line bg-sand-deep/70 text-muted',
  positive: 'border-soft/35 bg-soft/12 text-soft',
  warning: 'border-gold/45 bg-gold/12 text-gold-deep',
  critical: 'border-deep/25 bg-deep text-sand',
  gold: 'border-gold bg-gold text-deep'
};
const statusTones: Record<string, Tone> = {
  active: 'positive',
  published: 'positive',
  resolved: 'positive',
  ready: 'positive',
  approved: 'positive',
  sent: 'positive',
  new: 'gold',
  pending: 'warning',
  reviewing: 'warning',
  scheduled: 'warning',
  processing: 'warning',
  testing: 'warning',
  draft: 'neutral',
  comingSoon: 'neutral',
  disabled: 'neutral',
  retired: 'neutral',
  inactive: 'neutral',
  invited: 'neutral',
  rejected: 'neutral',
  hidden: 'neutral',
  suspended: 'critical',
  removed: 'critical',
  escalated: 'critical',
  failed: 'critical'
};
export function StatusBadge({
  status,
  label
}: {
  status: string;
  label: string;
}) {
  const tone = statusTones[status] ?? 'neutral';
  return <span className={`bee-overline inline-flex items-center whitespace-nowrap rounded-pill border px-2 py-[3px] text-[8.5px] font-bold ${toneClass[tone]}`} data-id="element-285" data-mp="6p50qu0yiqwz">
      {label}
    </span>;
}
export function UserCell({
  user,
  secondary
}: {
  user: Pick<AdminUser, 'id' | 'name' | 'username'>;
  secondary?: string;
}) {
  const index = adminUsers.findIndex(item => item.id === user.id);
  const avatar = people[(index < 0 ? 0 : index) % people.length].avatar;
  return <span className="flex min-w-0 items-center gap-2" data-id="element-286" data-mp="2sw04rev4ppm2">
      <Avatar src={avatar} name={user.name} size={34} data-id="element-287" data-mp="2suf9fn0zq2n5" />
      <span className="min-w-0" data-id="element-288" data-mp="2nx6ml94wdafk">
        <span className="block truncate text-[13px] font-medium text-deep" data-id="element-289" data-mp="2nw3yta2vfo3v">
          {user.name}
        </span>
        <span className="block truncate text-[11px] text-muted" data-id="element-290" data-mp="2izdjwq2syyk1">
          {secondary ?? user.username}
        </span>
      </span>
    </span>;
}
export function ContentPreview({
  excerpt,
  meta,
  kind
}: {
  excerpt: string;
  meta?: string;
  kind?: string;
}) {
  return <span className="flex min-w-0 items-start gap-2" data-id="element-291" data-mp="24z2rvjprbdxa">
      <span className="mt-[3px] grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-sand-deep text-[8.5px] font-bold uppercase tracking-wide text-muted" aria-hidden="true" data-id="element-292" data-mp="85r6tht1h9dr">
        {(kind ?? 'txt').slice(0, 3)}
      </span>
      <span className="min-w-0" data-id="element-293" data-mp="1xm4o3wkg0ao2">
        <span className="line-clamp-2 block text-[12.5px] leading-snug text-deep" data-id="element-294" data-mp="1xl1x7on4e885">
          {excerpt}
        </span>
        {meta && <span className="mt-[2px] block text-[11px] text-muted" data-id="element-295" data-mp="1np8ned0scsu2">{meta}</span>}
      </span>
    </span>;
}

/* ---------------------------------------------------------------- *
 * Metrics
 * ---------------------------------------------------------------- */

export function AdminMetricCard({
  label,
  value,
  delta,
  hint,
  accent = false
}: {
  label: string;
  value: string;
  delta?: number;
  hint?: string;
  accent?: boolean;
}) {
  const up = (delta ?? 0) >= 0;
  return <div className={`flex h-full flex-col rounded-card border p-3 ${accent ? 'border-gold/45 bg-white' : 'border-sand-line bg-white/80'}`} data-id="element-296" data-mp="2beb0r8odzzir">
      <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-297" data-mp="uyq90fum351c">{label}</span>
      <span className="mt-2 flex items-baseline gap-2" data-id="element-298" data-mp="zzxlutbm2uev">
        <span className="bee-num text-[26px] font-bold leading-none text-deep" data-id="element-299" data-mp="3e6nc53fp92hr">
          {value}
        </span>
        {delta !== undefined && <span className={`bee-num flex items-center gap-[2px] text-[11.5px] font-bold ${up ? 'text-soft' : 'text-gold-deep'}`} data-id="element-300" data-mp="397ancd59plcj">
            {up ? <TrendingUpIcon size={13} data-id="element-301" data-mp="3j7bbqdhzurnl" /> : <TrendingDownIcon size={13} data-id="element-302" data-mp="3j3vt50p430hf" />}
            {up ? '+' : ''}
            {delta.toFixed(1)}%
          </span>}
      </span>
      {hint && <span className="mt-auto pt-2 text-[11px] leading-snug text-muted" data-id="element-303" data-mp="2qidtubhp2wvd">{hint}</span>}
    </div>;
}

/* ---------------------------------------------------------------- *
 * Filters
 * ---------------------------------------------------------------- */

export function AdminSearch({
  value,
  onChange,
  placeholder
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return <label className="flex h-10 min-w-[220px] flex-1 items-center gap-2 rounded-card border border-sand-line bg-white px-2" data-id="element-304" data-mp="27ql18i6jfdo6">
      <SearchIcon size={15} className="shrink-0 text-muted" aria-hidden="true" data-id="element-305" data-mp="31kp77fmky7zv" />
      <input type="search" value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} aria-label={placeholder} className="min-w-0 flex-1 bg-transparent text-[13px] text-deep placeholder:text-muted focus:outline-none" data-id="element-306" data-mp="31mt9jt4tyyy1" />
    </label>;
}
export function FilterBar({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`flex flex-wrap items-center gap-2 rounded-card border border-sand-line bg-white/70 p-2 ${className}`} data-id="element-307" data-mp="3pa1f8c668a0l">
      {children}
    </div>;
}
export function FilterSelect({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: {
    id: string;
    label: string;
  }[];
  onChange: (value: string) => void;
}) {
  return <label className="flex h-10 items-center gap-2 rounded-card border border-sand-line bg-white px-2" data-id="element-308" data-mp="h8li2gizojk9">
      <span className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-309" data-mp="c9s4ko3pcsk3">{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)} className="bg-transparent text-[12.5px] font-medium text-deep focus:outline-none" data-id="element-310" data-mp="7afkgb47tzl7">
        {options.map(option => <option key={option.id} value={option.id} data-id="element-311" data-mp="zzcphcjf1zw0">
            {option.label}
          </option>)}
      </select>
    </label>;
}
export function DateRange({
  value,
  onChange
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const {
    t
  } = useLocale();
  const ranges = (['7d', '30d', '90d', '12m'] as const).map(id => ({
    id,
    label: t(`adm.range.${id}`)
  }));
  return <div className="flex h-10 items-center gap-1 rounded-card border border-sand-line bg-white px-1" data-id="element-312" data-mp="1iqmc8ni76l5j">
      <CalendarIcon size={15} className="mx-1 shrink-0 text-muted" aria-hidden="true" data-id="element-313" data-mp="1dxt5xv72z4rc" />
      {ranges.map(range => <button key={range.id} type="button" aria-pressed={value === range.id} onClick={() => onChange(range.id)} className={`h-8 whitespace-nowrap rounded-[8px] px-2 text-[12px] font-medium transition-colors duration-150 ease-brand ${value === range.id ? 'bg-deep text-sand' : 'text-muted hover:text-deep'}`} data-id="element-314" data-mp="1gyr77t19eghh">
          {range.label}
        </button>)}
    </div>;
}

/* ---------------------------------------------------------------- *
 * Global states
 * ---------------------------------------------------------------- */

function StateBlock({
  icon,
  title,
  body,
  action,
  tone = 'light'
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return <div className={`flex flex-col items-center rounded-card border px-4 py-8 text-center ${dark ? 'border-deep bg-deep' : 'border-sand-line bg-white/70'}`} data-id="element-315" data-mp="2dp08hitua09g">
      <span className={`grid h-14 w-14 place-items-center rounded-pill ${dark ? 'border border-gold/45 bg-deeper text-gold' : 'bg-sand-deep text-soft'}`} data-id="element-316" data-mp="329d52h965x16">
        {icon}
      </span>
      <h2 className={`mt-3 text-[16px] font-bold ${dark ? 'text-sand' : 'text-deep'}`} data-id="element-317" data-mp="2sanrv1s7x0r9">
        {title}
      </h2>
      <p className={`mt-1 max-w-[46ch] text-[12.5px] leading-relaxed ${dark ? 'text-ondark' : 'text-muted'}`} data-id="element-318" data-mp="2eir4tc5eph1q">
        {body}
      </p>
      {action && <div className="mt-3 flex items-center gap-2" data-id="element-319" data-mp="24m4kqpkwmu0r">{action}</div>}
    </div>;
}
export function AdminEmptyState({
  title,
  body,
  action
}: {
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return <StateBlock icon={<InboxIcon size={22} data-id="element-321" data-mp="1vlg55qfv4llr" />} title={title} body={body} action={action} data-id="element-320" data-mp="1vm7rsbrhf8zw" />;
}
export function AdminErrorState({
  title,
  body,
  onRetry
}: {
  title: string;
  body: string;
  onRetry?: () => void;
}) {
  const {
    t
  } = useLocale();
  return <StateBlock icon={<CircleAlertIcon size={22} data-id="element-323" data-mp="1csxxvncgk96" />} title={title} body={body} action={onRetry ? <Button variant="outline" onClick={onRetry} data-id="element-324" data-mp="3sk5dk68kwpmz">
            {t('adm.action.retry')}
          </Button> : undefined} data-id="element-322" data-mp="1dccd2dscqig" />;
}
export function PermissionGate({
  allowed,
  area,
  children
}: {
  allowed: boolean;
  area: string;
  children: React.ReactNode;
}) {
  const {
    t
  } = useLocale();
  if (allowed) return <>{children}</>;
  return <StateBlock icon={<LockIcon size={22} data-id="element-326" data-mp="f3pj51exxz67" />} title={t('adm.permission.title')} body={t('adm.permission.body', {
    area
  })} tone="dark" data-id="element-325" data-mp="f47gk1dcajfp" />;
}

/* ---------------------------------------------------------------- *
 * Overlays
 * ---------------------------------------------------------------- */

export function ConfirmationModal({
  open,
  title,
  body,
  confirmLabel,
  destructive = false,
  requireText,
  onConfirm,
  onClose
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
  /** When set, the admin must type this exact word to enable confirmation. */
  requireText?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const {
    t
  } = useLocale();
  const [typed, setTyped] = React.useState('');
  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  if (!open) return null;
  const blocked = requireText ? typed.trim() !== requireText : false;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-327" data-mp="1z2rbb9nx2k6s">
      <button type="button" aria-label={t('adm.action.close')} onClick={onClose} className="absolute inset-0 bg-deeper/70" data-id="element-328" data-mp="232rpct73stdb" />
      <div role="dialog" aria-modal="true" aria-label={title} className="relative w-full max-w-[460px] rounded-panel border border-sand-line bg-sand p-4 shadow-lift" data-id="element-329" data-mp="8oqq477n64cp">
        <span className={`grid h-11 w-11 place-items-center rounded-pill ${destructive ? 'bg-deep text-gold' : 'bg-sand-deep text-soft'}`} data-id="element-330" data-mp="3ehqbute8nvlp">
          {destructive ? <AlertTriangleIcon size={19} data-id="element-331" data-mp="3ogz0r7u8yk17" /> : <CircleAlertIcon size={19} data-id="element-332" data-mp="3og76c0cdysci" />}
        </span>
        <h2 className="mt-2 text-[17px] font-bold text-deep" data-id="element-333" data-mp="3jh2x59pgoum6">{title}</h2>
        <p className="mt-1 text-[12.5px] leading-relaxed text-muted" data-id="element-334" data-mp="3jfrvbffktwxp">{body}</p>

        {requireText && <label className="mt-3 block" data-id="element-335" data-mp="2qsyqolz2gdif">
            <span className="block text-[11.5px] font-medium text-deep" data-id="element-336" data-mp="2qu03ovzn7a2s">
              {t('adm.confirm.type', {
            word: requireText
          })}
            </span>
            <input value={typed} onChange={event => setTyped(event.target.value)} className="mt-1 h-10 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep focus:border-deep/40 focus:outline-none" data-id="element-337" data-mp="30r4ooshr4zi0" />
          </label>}

        <div className="mt-4 flex items-center justify-end gap-2" data-id="element-338" data-mp="nkomchkupsil">
          <Button variant="outline" onClick={onClose} data-id="element-339" data-mp="nibu9bldq1rt">
            {t('adm.action.cancel')}
          </Button>
          <Button variant={destructive ? 'danger' : 'gold'} disabled={blocked} onClick={onConfirm} data-id="element-340" data-mp="scy8dmiui8fl">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>;
}
export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const {
    t
  } = useLocale();
  if (!open) return null;
  return <div className="fixed inset-0 z-50 flex justify-end" data-id="element-341" data-mp="x9ydnv7vf75n">
      <button type="button" aria-label={t('adm.action.close')} onClick={onClose} className="absolute inset-0 bg-deeper/70" data-id="element-342" data-mp="11b1t4gm0b16c" />
      <aside role="dialog" aria-modal="true" aria-label={title} className="relative flex h-full w-full max-w-[520px] flex-col border-l border-sand-line bg-sand shadow-lift" data-id="element-343" data-mp="3i2tfnhxn7krf">
        <header className="flex items-start gap-3 border-b border-sand-line px-4 py-3" data-id="element-344" data-mp="1nk8nkgx5b9gd">
          <div className="min-w-0 flex-1" data-id="element-345" data-mp="1nml20k3p31oo">
            <h2 className="text-[16px] font-bold leading-tight text-deep" data-id="element-346" data-mp="1isgspu6yihkf">{title}</h2>
            {subtitle && <p className="mt-[2px] text-[11.5px] text-muted" data-id="element-347" data-mp="1dtw28mhibc96">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label={t('adm.action.close')} className="grid h-9 w-9 shrink-0 place-items-center rounded-pill text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-348" data-mp="19ueutniis51d">
            <XIcon size={17} data-id="element-349" data-mp="uwmeit55fcm4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-3" data-id="element-350" data-mp="2u8f4i5tirhpc">{children}</div>
        {footer && <footer className="flex items-center justify-end gap-2 border-t border-sand-line px-4 py-3" data-id="element-351" data-mp="2p9u5g9q0742h">
            {footer}
          </footer>}
      </aside>
    </div>;
}

/* ---------------------------------------------------------------- *
 * Pagination
 * ---------------------------------------------------------------- */

export function Pagination({
  page,
  pageCount,
  total,
  onPage
}: {
  page: number;
  pageCount: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const {
    t
  } = useLocale();
  return <div className="flex flex-wrap items-center justify-between gap-2 border-t border-sand-line px-3 py-2" data-id="element-352" data-mp="2et1zx3z25m5j">
      <p className="bee-num text-[11.5px] text-muted" data-id="element-353" data-mp="2itu9xzfr7v2v">
        {t('adm.table.records', {
        total,
        page,
        pages: pageCount
      })}
      </p>
      <div className="flex items-center gap-1" data-id="element-354" data-mp="2nurqk7l48h29">
        <button type="button" onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1} aria-label={t('adm.table.prev')} className="grid h-9 w-9 place-items-center rounded-card border border-sand-line bg-white text-muted transition-colors duration-150 ease-brand hover:text-deep disabled:opacity-40" data-id="element-355" data-mp="2ssjfqtudlp2r">
          <ChevronLeftIcon size={16} data-id="element-356" data-mp="p3zuppi1kepl" />
        </button>
        <button type="button" onClick={() => onPage(Math.min(pageCount, page + 1))} disabled={page >= pageCount} aria-label={t('adm.table.next')} className="grid h-9 w-9 place-items-center rounded-card border border-sand-line bg-white text-muted transition-colors duration-150 ease-brand hover:text-deep disabled:opacity-40" data-id="element-357" data-mp="p5kr7knx27q3">
          <ChevronRightIcon size={16} data-id="element-358" data-mp="17tprgi5d7ak5" />
        </button>
      </div>
    </div>;
}

/* ---------------------------------------------------------------- *
 * Audit
 * ---------------------------------------------------------------- */

export function AuditEntry({
  admin,
  role,
  action,
  entity,
  timestamp,
  context,
  before,
  after,
  sensitive
}: {
  admin: string;
  role: string;
  action: string;
  entity: string;
  timestamp: string;
  context: string;
  before?: string;
  after?: string;
  sensitive?: boolean;
}) {
  return <li className="flex gap-3 border-b border-sand-line py-3 last:border-b-0" data-id="element-359" data-mp="1y6kt30184cew">
      <span className={`mt-[3px] h-[9px] w-[9px] shrink-0 rounded-pill ${sensitive ? 'bg-gold' : 'bg-soft/60'}`} aria-hidden="true" data-id="element-360" data-mp="235fjan1it391" />
      <div className="min-w-0 flex-1" data-id="element-361" data-mp="1tc7177lrsnez">
        <p className="text-[13px] leading-snug text-deep" data-id="element-362" data-mp="1tb5hyfuloum6">
          <span className="font-bold" data-id="element-363" data-mp="1fcyxc1enwr4g">{admin}</span>{' '}
          <span className="text-muted" data-id="element-364" data-mp="1kck2wuoc7xq5">({role})</span> · {action}{' '}
          <span className="bee-num text-muted" data-id="element-365" data-mp="1kaqupay2t5g1">{entity}</span>
        </p>
        <p className="mt-[2px] text-[11.5px] leading-snug text-muted" data-id="element-366" data-mp="16en0hhn7cx3k">{context}</p>
        {(before || after) && <p className="bee-num mt-1 inline-flex flex-wrap items-center gap-1 text-[11px]" data-id="element-367" data-mp="1agqeoe19afsj">
            <span className="rounded-[6px] bg-sand-deep px-[6px] py-[2px] text-muted" data-id="element-368" data-mp="30ruz7keatsuh">
              {before ?? '—'}
            </span>
            <ChevronRightIcon size={12} className="text-muted" aria-hidden="true" data-id="element-369" data-mp="1ozu92b8gey7o" />
            <span className="rounded-[6px] bg-deep px-[6px] py-[2px] text-sand" data-id="element-370" data-mp="1oxpumzcxigt5">
              {after ?? '—'}
            </span>
          </p>}
      </div>
      <span className="bee-num shrink-0 text-[11px] text-muted" data-id="element-371" data-mp="1f2pnaciit4re">{timestamp}</span>
    </li>;
}